import app from '../../forum/app';
import PaginatedListState, { Page, PaginatedListParams, PaginatedListRequestParams } from '../../common/states/PaginatedListState';
import Notification from '../../common/models/Notification';
import AllConfirmModal from '../../common/dianliang/AllConfirmModal';
import AlertUtil from '../../forum/dianLiang/alertUtil';
import type { ApiResponsePlural } from '../../common/Store';

export interface DiscussionListParams extends PaginatedListParams {
  sort?: string;
}

export type NotificationCategory = 'reply' | 'like' | 'system';

export interface NotificationUnreadCounts {
  reply: number;
  like: number;
  system: number;
}

export default class NotificationListState<P extends DiscussionListParams = DiscussionListParams> extends PaginatedListState<Notification> {
  unreadCounts: NotificationUnreadCounts;

  constructor(params: P) {
    super(params, 1, 20);

    this.unreadCounts = {
      reply: 0,
      like: 0,
      system: 0,
    };
  }
  
  get type(): string {
    return 'notifications';
  }

  /**
   * Load the next page of notification results.
   */
  load(): Promise<void> {
    // if (app.session.user?.newNotificationCount()) {
    //   this.pages = [];
    //   this.location = { page: 1 };
    // }
    this.pages = [];
    this.location = { page: 1 };

    if (this.pages.length > 0) {
      return Promise.resolve();
    }

    app.session.user?.pushAttributes({ newNotificationCount: 0 });

    return super.loadNext();
  }

  clear(): void {
    super.clear();
    
    this.params.filter = {};
  }

  requestParams(): PaginatedListRequestParams {
    const params = {
      filter: this.params.filter || {},
    };
    
    if (this.params.q) {
      params.filter.q = this.params.q;
    }

    return params;
  }

  protected parseResults(page: number, results: ApiResponsePlural<Notification>): void {
    const unreadCounts = results.payload?.meta?.unreadCounts as Partial<NotificationUnreadCounts> | undefined;

    if (unreadCounts) {
      this.unreadCounts = {
        reply: Number(unreadCounts.reply) || 0,
        like: Number(unreadCounts.like) || 0,
        system: Number(unreadCounts.system) || 0,
      };
      app.cache.notificationUnreadCounts = { ...this.unreadCounts };
    }

    super.parseResults(page, results);
  }

  markOneAsRead(contentType: string): void {
    const category = this.categoryForContentType(contentType);
    this.setUnreadCount(category, this.unreadCounts[category] - 1);
    this.syncUserUnreadCounts();

    if (category === 'reply') {
      app.session.user?.pushAttributes({ unreadPostNotificationCount: this.unreadCounts.reply });
    } else if (category === 'like') {
      app.session.user?.pushAttributes({ unreadLikeNotificationCount: this.unreadCounts.like });
    }
  }

  private categoryForContentType(contentType: string): NotificationCategory {
    if (contentType === 'newPost' || contentType === 'userMentioned') return 'reply';
    if (contentType === 'postLiked') return 'like';

    return 'system';
  }

  private categoryForControlType(type: number): NotificationCategory | undefined {
    if (type === 1) return 'reply';
    if (type === 2) return 'like';
    if (type === 4) return 'system';

    return undefined;
  }

  private setUnreadCount(category: NotificationCategory, count: number): void {
    this.unreadCounts[category] = Math.max(0, count);
    app.cache.notificationUnreadCounts = { ...this.unreadCounts };
  }

  private syncUserUnreadCounts(): void {
    app.session.user?.pushAttributes({
      unreadNotificationCount: this.unreadCounts.reply + this.unreadCounts.like + this.unreadCounts.system,
    });
  }

  private clearUnreadCount(type: number): void {
    const category = this.categoryForControlType(type);

    if (category) {
      this.setUnreadCount(category, 0);
      this.syncUserUnreadCounts();

      if (category === 'reply') app.session.user?.pushAttributes({ unreadPostNotificationCount: 0 });
      if (category === 'like') app.session.user?.pushAttributes({ unreadLikeNotificationCount: 0 });
    } else {
      this.unreadCounts = { reply: 0, like: 0, system: 0 };
      app.cache.notificationUnreadCounts = { ...this.unreadCounts };
      app.session.user?.pushAttributes({
        unreadNotificationCount: 0,
        unreadPostNotificationCount: 0,
        unreadLikeNotificationCount: 0,
      });
    }
  }

  /**
   * Mark all of the notifications as read.
   */
  markAllAsRead(type : number) {
    if (this.pages.length === 0) return;
    
    app.modal.show(AllConfirmModal, {
      title: app.translator.trans('dl-response.forum.sure_read_modal.warn_title'),
      text: app.translator.trans('dl-response.forum.sure_read_modal.please_sure_read'),
      promise: true,
      onconfirm: (resolve, reject) => {
        this.clearUnreadCount(type);

        this.pages.forEach((page) => {
          page.items.forEach((notification) => notification.pushAttributes({ isRead: true }));
        });

        return app.request({
          url: app.forum.attribute('apiUrl') + '/notifications/read',
          method: 'POST',
          body: {type:type ? type : 0},
        }).then(result => {
          app.cache.floatingSystemNotifications = null;
          m.redraw();
          AlertUtil.onSuccess(app, app.translator.trans('dl_discussion_controller.forum.pushFrontModal.admin_submit_success'));
        });
      },
    }, true)

    
  }

  /**
   * Delete all of the notifications for this user.
   */
  deleteAll(type : number) {
    if (this.pages.length === 0) return;

    app.modal.show(AllConfirmModal, {
      title: app.translator.trans('dl-response.forum.sure_read_modal.warn_title'),
      text: app.translator.trans('dl-response.forum.sure_read_modal.please_sure_delete'),
      promise: true,
      onconfirm: (resolve, reject) => {
        this.clearUnreadCount(type);

        this.pages = [];

        return app.request({
          url: app.forum.attribute('apiUrl') + '/notifications',
          method: 'DELETE',
          body: {type:type ? type : 0},
        }).then(result => {
          app.cache.floatingSystemNotifications = null;
          app.cache.floatingRewardNotifications = null;
          m.redraw();
          AlertUtil.onSuccess(app, app.translator.trans('dl_discussion_controller.forum.pushFrontModal.admin_submit_success'));
        });
        },
      }, true)

    
  }
}
